/** Contracts × stages grid for the dashboard Overview tab.
 *
 *  One row per contract showing the latest review_state per stage with a
 *  color-coded dot (approved/draft/failed/missing). Auto-refreshes every
 *  5 minutes so the reviewer sees pipeline progress on auto-flow jobs
 *  without manually reloading.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CheckCircle2, Circle, XCircle, MinusCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api, ApiError } from '@/lib/api';

type StageStatus = 'approved' | 'draft' | 'failed' | 'active' | 'missing';

interface ContractRow {
  id: string;
  job_id: string;
  title: string;
  version: number | null;
  lifecycle_step: number | null;
  status: string;
  updated_at: string | null;
  stages: Record<string, StageStatus>;
  comments?: Record<string, boolean>;
  counts: {
    approved: number; draft: number; failed: number; active?: number;
    missing: number; commented?: number; total: number;
  };
}

interface OverviewResponse {
  contracts: ContractRow[];
  stage_order: string[];
}

const STAGE_LABEL: Record<string, string> = {
  document_assistant: 'OCR',
  document: 'Doc',
  parties: 'Par',
  clause: 'Cls',
  gate_1: 'G1',
  compliance: 'Cmp',
  signature: 'Sig',
  gate_2: 'G2',
  gate: 'Gate',
  orchestrator_bootstrap: 'Final',
};

const STATUS_COLOR: Record<StageStatus, string> = {
  approved: 'bg-green-500',
  draft: 'bg-yellow-400',
  failed: 'bg-red-500',
  active: 'bg-blue-500 animate-pulse ring-2 ring-blue-300',
  missing: 'bg-muted-foreground/30',
};

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function StageDot({ stage, status, hasComment }: { stage: string; status: StageStatus; hasComment?: boolean }) {
  // Purple ring overlay when reviewer left a comment on this stage —
  // orthogonal to the base status colour. Implemented as an outer ring
  // box around the coloured inner dot so both signals coexist.
  if (hasComment) {
    return (
      <div
        title={`${STAGE_LABEL[stage] ?? stage}: ${status} · has reviewer comment`}
        className="inline-flex items-center justify-center h-4 w-4 rounded-full ring-2 ring-violet-500"
        aria-label={`${stage} ${status} with reviewer comment`}
      >
        <div className={`h-2.5 w-2.5 rounded-full ${STATUS_COLOR[status]}`} />
      </div>
    );
  }
  return (
    <div
      title={`${STAGE_LABEL[stage] ?? stage}: ${status}`}
      className={`h-2.5 w-2.5 rounded-full ${STATUS_COLOR[status]}`}
      aria-label={`${stage} ${status}`}
    />
  );
}

export function ContractsOverviewWidget() {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    api
      .get<OverviewResponse>('/api/v1/dashboard/contracts-overview')
      .then((r) => {
        setData(r);
        setLastFetched(new Date());
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load overview.');
      })
      .finally(() => { setLoading(false); });
  }

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => { clearInterval(id); };
  }, []);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            Contracts overview
            <Badge variant="outline" className="text-[10px] font-normal">
              auto-refresh 5 min
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Per-stage status: <span className="text-green-700 font-medium">green</span> = human-approved,
            {' '}<span className="text-yellow-700 font-medium">yellow</span> = agent draft awaiting human approval,
            {' '}<span className="text-blue-700 font-medium">pulsing blue</span> = agent running now,
            {' '}<span className="text-red-700 font-medium">red</span> = failed,
            {' '}grey = not yet reached. <span className="text-violet-700 font-medium">Purple ring</span> = reviewer left a comment.
            {' '}Click a row to open.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          aria-label="Refresh now"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {lastFetched && (
            <span className="font-mono">
              {lastFetched.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </button>
      </CardHeader>
      <CardContent className="pt-0">
        {error && (
          <p role="alert" className="text-xs text-destructive py-3">{error}</p>
        )}
        {!data && !error && (
          <p className="text-xs text-muted-foreground py-3">Loading…</p>
        )}
        {data && data.contracts.length === 0 && (
          <p className="text-xs text-muted-foreground py-3">No contracts yet.</p>
        )}
        {data && data.contracts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground text-left border-b">
                  <th className="py-1.5 pr-3 font-medium">Contract</th>
                  {data.stage_order.map((s) => (
                    <th key={s} className="py-1.5 px-1 font-medium text-center" title={s}>
                      {STAGE_LABEL[s] ?? s}
                    </th>
                  ))}
                  <th className="py-1.5 px-2 font-medium text-right">Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c) => {
                  const approved = c.counts.approved;
                  const total = c.counts.total;
                  const allDone = approved === total;
                  return (
                    <tr key={c.id} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="py-1.5 pr-3 max-w-[28ch]">
                        <Link
                          to={`/contracts/${c.id}`}
                          className="text-foreground hover:underline truncate inline-block max-w-full"
                          title={c.title}
                        >
                          {c.title}
                          {c.version != null && c.version > 1 && (
                            <span className="ml-1 text-muted-foreground">v{c.version}</span>
                          )}
                        </Link>
                      </td>
                      {data.stage_order.map((s) => (
                        <td key={s} className="py-1.5 px-1 text-center">
                          <div className="inline-flex">
                            <StageDot
                              stage={s}
                              status={c.stages[s] ?? 'missing'}
                              hasComment={c.comments?.[s] === true}
                            />
                          </div>
                        </td>
                      ))}
                      <td className="py-1.5 px-2 text-right whitespace-nowrap">
                        <span className={`font-mono ${allDone ? 'text-green-700' : c.counts.failed > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                          {approved}/{total}
                        </span>
                        {allDone && <CheckCircle2 className="inline-block h-3 w-3 ml-1 text-green-600" />}
                        {c.counts.failed > 0 && <XCircle className="inline-block h-3 w-3 ml-1 text-red-600" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-600"/> human-approved</span>
              <span className="inline-flex items-center gap-1"><Circle className="h-3 w-3 fill-yellow-400 text-yellow-400"/> draft (awaiting review)</span>
              <span className="inline-flex items-center gap-1"><Circle className="h-3 w-3 fill-blue-500 text-blue-500 animate-pulse"/> agent running</span>
              <span className="inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-red-600"/> failed</span>
              <span className="inline-flex items-center gap-1"><MinusCircle className="h-3 w-3 text-muted-foreground"/> not yet reached</span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full ring-2 ring-violet-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                </span>
                with reviewer comment
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
